import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { VideoOff, X } from 'lucide-react';
import { cn } from '../lib/cn.js';
import { Button } from './button.js';
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle } from './dialog.js';
export const VIDEO_PLAYER_UNAVAILABLE_RU = 'Видео недоступно: файла нет на сервере.';
/**
 * Полный просмотр видео в модальном окне: нативные controls, вертикальное
 * 9:16, по умолчанию стартует сразу и без звука.
 *
 * Закрытие ✕, кликом по фону и **Esc ставит видео на паузу** — в старом
 * дашборде Esc закрывал окно, а беззвучное видео продолжало играть скрытым.
 * Здесь пауза — в обработчике закрытия, до размонтирования, по любому пути.
 * Фокус заперт в окне (Radix Dialog) и возвращается туда, откуда открыли.
 */
export function VideoPlayerModal({ open, onOpenChange, src, poster, title, description, children, startMuted = true, autoPlay = true, loop = true, }) {
    const videoRef = React.useRef(null);
    const [failed, setFailed] = React.useState(false);
    React.useEffect(() => {
        setFailed(false);
    }, [src]);
    const pause = React.useCallback(() => {
        videoRef.current?.pause();
    }, []);
    // Закрытие сверху (`open` стал false не через наш обработчик) — тоже пауза.
    React.useEffect(() => {
        if (!open)
            pause();
    }, [open, pause]);
    const handleOpenChange = (next) => {
        if (!next)
            pause();
        onOpenChange(next);
    };
    // `muted` как атрибут React не выставляет надёжно — задаём свойством при
    // монтировании, ДО autoplay, иначе браузер заблокирует автостарт со звуком.
    const setVideo = React.useCallback((el) => {
        videoRef.current = el;
        if (el)
            el.muted = startMuted;
    }, [startMuted]);
    const withSide = children != null;
    return (_jsx(Dialog, { open: open, onOpenChange: handleOpenChange, children: _jsxs(DialogContent, { size: withSide ? 'lg' : 'sm', className: "gap-3 p-4", ...(description == null ? { 'aria-describedby': undefined } : {}), children: [_jsxs("div", { className: "flex items-start justify-between gap-3", children: [_jsxs("div", { className: "flex min-w-0 flex-col gap-0.5", children: [_jsx(DialogTitle, { className: "truncate text-base", children: title }), description != null ? _jsx(DialogDescription, { children: description }) : null] }), _jsx(DialogClose, { asChild: true, children: _jsx(Button, { type: "button", variant: "ghost", size: "icon", "aria-label": "\u0417\u0430\u043A\u0440\u044B\u0442\u044C", children: _jsx(X, { "aria-hidden": true, className: "h-4 w-4" }) }) })] }), _jsxs("div", { className: cn('flex gap-4', withSide ? 'flex-col sm:flex-row' : 'flex-col'), children: [_jsx("div", { className: "mx-auto flex aspect-[9/16] h-[min(70dvh,40rem)] max-w-full shrink-0 items-center justify-center overflow-hidden rounded-[var(--radius-3)] bg-[var(--color-scrim)]", children: failed ? (_jsxs("span", { className: "flex flex-col items-center gap-2 p-4 text-center text-sm text-[var(--color-text-on-accent)]", children: [_jsx(VideoOff, { "aria-hidden": true, className: "h-8 w-8" }), VIDEO_PLAYER_UNAVAILABLE_RU] })) : (_jsx("video", { ref: setVideo, src: src, poster: poster ?? undefined, controls: true, autoPlay: autoPlay, muted: startMuted, loop: loop, playsInline: true, preload: "metadata", onError: () => setFailed(true), className: "h-full w-full object-contain" })) }), withSide ? _jsx("div", { className: "min-w-0 flex-1 overflow-y-auto", children: children }) : null] })] }) }));
}
//# sourceMappingURL=video-player-modal.js.map